import React, { useCallback, useMemo } from 'react';
import { shape, array, string, bool, number, object, func } from 'prop-types';
import { FormattedMessage } from '../../../../../util/reactIntl';
import { buildSearchQuery } from '../../../../../util/urlHelpers';
import { NamedLink, IconArrowHead, AspectRatioWrapper } from '../../../../../components';
import { getCloudinaryBaseUrl } from '../../../../../util/cloudinary';
import classNames from 'classnames';

import css from './DropdownMenu.module.css';

const LEFT_PANEL_MAX_VISIBLE_CATEGORIES = 8;
const RIGHT_PANEL_MAX_VISIBLE_CATEGORIES = 4;

// Defined at the top of the component since it is used across multiple separate components
// that display images with the same variantPrefix.
const cloudinaryBaseUrl = getCloudinaryBaseUrl({
  variantPrefix: 'landscape-crop2x',
});

const CategoryItem = React.memo(props => {
  const {
    className,
    category,
    location,
    isActive,
    onMouseEnter,
    hoveredCategoryLevel,
    categoryKey,
  } = props;
  const classes = classNames(css.categoryItem, className, { [css.active]: isActive });
  const imagePath = `${cloudinaryBaseUrl}/categories/${categoryKey}/${category.id}.jpg`;
  return (
    <li className={classes} onMouseEnter={onMouseEnter}>
      <NamedLink
        className={css.categoryLink}
        name="SearchPage"
        to={{ search: buildSearchQuery(hoveredCategoryLevel, categoryKey, category.id, location) }}
      >
        <div className={css.categoryImageWrapper}>
          <img className={css.categoryImage} src={imagePath} alt={category.name} />
        </div>
        {category.name}
        <IconArrowHead className={css.arrowIcon} direction="right" />
      </NamedLink>
    </li>
  );
});

const locationShape = {
  search: string.isRequired,
};
const locationPropType = shape(locationShape);

const hoveredCategoryLevelShape = {
  categoryLevel1: string,
  categoryLevel2: string,
  categoryLevel3: string,
};
const hoveredCategoryLevelPropType = shape(hoveredCategoryLevelShape);

CategoryItem.propTypes = {
  className: string,
  category: object.isRequired,
  isActive: bool.isRequired,
  onMouseEnter: func.isRequired,
  hoveredCategoryLevel: hoveredCategoryLevelPropType.isRequired,
  categoryKey: string.isRequired,
  location: locationPropType.isRequired,
};

const LeftPanel = React.memo(props => {
  const {
    location,
    categories,
    categoryTitle,
    hoveredCategoryLevel,
    setHoveredCategoryLevel,
    maxVisibleCategories = LEFT_PANEL_MAX_VISIBLE_CATEGORIES,
  } = props;

  const handleMouseEnter = useCallback(
    categoryId => {
      setHoveredCategoryLevel(prevState => ({
        ...prevState,
        categoryLevel2: categoryId,
        categoryLevel3: null,
      }));
    },
    [setHoveredCategoryLevel]
  );

  const visibleCategories = useMemo(() => categories.slice(0, maxVisibleCategories), [
    categories,
    maxVisibleCategories,
  ]);
  const hasViewAllLink = categories.length > maxVisibleCategories;

  return (
    <div className={css.leftPanel}>
      <h4 className={css.categoryTitle}>{categoryTitle}</h4>
      <ul className={css.categoryList}>
        {visibleCategories.map(category => (
          <CategoryItem
            key={category.id}
            category={category}
            location={location}
            isActive={hoveredCategoryLevel.categoryLevel2 === category.id}
            onMouseEnter={() => handleMouseEnter(category.id)}
            hoveredCategoryLevel={hoveredCategoryLevel}
            categoryKey="categoryLevel2"
          />
        ))}
        {hasViewAllLink && (
          <li
            className={classNames(css.categoryItem, css.itemViewAll)}
            onMouseEnter={() => handleMouseEnter(null)}
          >
            <NamedLink
              className={css.categoryLink}
              name="SearchPage"
              to={{
                search: buildSearchQuery(hoveredCategoryLevel, null, null, location),
              }}
            >
              <FormattedMessage id="TopbarNavigation.DropdownMenu.viewAll" />
            </NamedLink>
          </li>
        )}
      </ul>
    </div>
  );
});

LeftPanel.propTypes = {
  categories: array.isRequired,
  categoryTitle: string.isRequired,
  hoveredCategoryLevel: hoveredCategoryLevelPropType.isRequired,
  setHoveredCategoryLevel: func.isRequired,
  maxVisibleCategories: number,
  location: locationPropType.isRequired,
};

const RightPanel = React.memo(props => {
  const {
    location,
    categories,
    categoryTitle,
    categoryImage,
    hoveredCategoryLevel,
    setHoveredCategoryLevel,
    maxVisibleCategories = RIGHT_PANEL_MAX_VISIBLE_CATEGORIES,
  } = props;

  const renderCategoryTitleImage = categoryImage && (
    <div className={css.categoryImageWrapper}>
      <img className={css.categoryImage} src={categoryImage} alt={categoryTitle} />
    </div>
  );

  const renderBigCategoryImage = (
    <AspectRatioWrapper className={css.categoryBigImageWrapper} width={4} height={3}>
      <img className={css.categoryBigImage} src={categoryImage} alt={categoryTitle} />
    </AspectRatioWrapper>
  );

  const handleMouseEnter = useCallback(
    categoryId => {
      setHoveredCategoryLevel(prevState => ({
        ...prevState,
        categoryLevel3: categoryId,
      }));
    },
    [setHoveredCategoryLevel]
  );

  const visibleCategories = useMemo(() => categories.slice(0, maxVisibleCategories), [
    categories,
    maxVisibleCategories,
  ]);
  const hasViewAllLink = categories.length > maxVisibleCategories;

  if (visibleCategories.length === 0) {
    return null;
  }

  return (
    <div className={css.rightPanel}>
      <h4 className={css.categoryTitle}>
        {renderCategoryTitleImage}
        {categoryTitle}
      </h4>
      <div className={css.rightPanelContentWrapper}>
        <ul className={css.categoryList}>
          {visibleCategories.map(category => (
            <CategoryItem
              key={category.id}
              className={css.rightPanelCategoryItem}
              category={category}
              location={location}
              isActive={hoveredCategoryLevel.categoryLevel3 === category.id}
              onMouseEnter={() => handleMouseEnter(category.id)}
              hoveredCategoryLevel={hoveredCategoryLevel}
              categoryKey="categoryLevel3"
            />
          ))}
          {hasViewAllLink && (
            <li
              className={classNames(css.categoryItem, css.itemViewAll)}
              onMouseEnter={() => handleMouseEnter(null)}
            >
              <NamedLink
                className={css.categoryLink}
                name="SearchPage"
                to={{
                  search: buildSearchQuery(
                    hoveredCategoryLevel,
                    'categoryLevel2',
                    hoveredCategoryLevel.categoryLevel2,
                    location
                  ),
                }}
              >
                <FormattedMessage id="TopbarNavigation.DropdownMenu.viewAll" />
              </NamedLink>
            </li>
          )}
        </ul>
        {renderBigCategoryImage}
      </div>
    </div>
  );
});

RightPanel.propTypes = {
  categories: array.isRequired,
  categoryTitle: string.isRequired,
  categoryImage: string.isRequired,
  hoveredCategoryLevel: hoveredCategoryLevelPropType.isRequired,
  setHoveredCategoryLevel: func.isRequired,
  maxVisibleCategories: number,
  location: locationPropType.isRequired,
};

const DropdownMenu = props => {
  const { location, categories, hoveredCategoryLevel, setHoveredCategoryLevel } = props;

  const categoryLevel1 = useMemo(
    () => categories.find(cat => cat.id === hoveredCategoryLevel.categoryLevel1),
    [categories, hoveredCategoryLevel.categoryLevel1]
  );
  const categoryLevel1Subcategories = categoryLevel1?.subcategories || [];
  const categoryLevel1Title = categoryLevel1?.name || '';

  const categoryLevel2 = useMemo(
    () => categoryLevel1Subcategories.find(cat => cat.id === hoveredCategoryLevel.categoryLevel2),
    [categoryLevel1Subcategories, hoveredCategoryLevel.categoryLevel2]
  );
  const categoryLevel2Subcategories = categoryLevel2?.subcategories || [];
  const categoryLevel2Title = categoryLevel2?.name || '';
  const categoryLevel2Image = categoryLevel2
    ? `${cloudinaryBaseUrl}/categories/categoryLevel2/${categoryLevel2.id}.jpg`
    : '';

  return (
    <div className={css.root}>
      <div className={css.dropdownMenu}>
        <LeftPanel
          location={location}
          categories={categoryLevel1Subcategories}
          categoryTitle={categoryLevel1Title}
          hoveredCategoryLevel={hoveredCategoryLevel}
          setHoveredCategoryLevel={setHoveredCategoryLevel}
        />
        <RightPanel
          location={location}
          categories={categoryLevel2Subcategories}
          categoryTitle={categoryLevel2Title}
          categoryImage={categoryLevel2Image}
          hoveredCategoryLevel={hoveredCategoryLevel}
          setHoveredCategoryLevel={setHoveredCategoryLevel}
        />
      </div>
    </div>
  );
};

DropdownMenu.propTypes = {
  categories: array.isRequired,
  hoveredCategoryLevel: hoveredCategoryLevelPropType.isRequired,
  setHoveredCategoryLevel: func.isRequired,
  location: locationPropType.isRequired,
};

export default DropdownMenu;
